import React, { useEffect, useState } from "react";
import { Company } from "../../types/addServiceTypes";
import * as IBAN from "iban";

interface BillingInfo {
  billing_id: number;
  company_id: number;
  billingEmail: string;
  iban: string;
  poNumber: string | null;
  twikeyRef: string;
  active_dd_mandate: number;
  eol_dd_mandate_uploaded: number;
  twikeyDate: string | null;
  twikeyCode: string | null;
  twikeyValid: number;
  billingAddressStreet: string;
  billingAddressNumber: string;
  billingAddressZip: string;
  billingAddressCity: string;
  billingAddressCountry: string;
  invoicingCycle: string;
  created_at: string;
  updated_at: string;
}

interface BillingEditModalProps {
  showModal: boolean;
  onClose: () => void;
  editingBillingInfo: BillingInfo | null;
  selectedUserCompany: Company | null;
  onSave: (billingInfo: BillingInfo) => void;
}

export const BillingEditModal: React.FC<BillingEditModalProps> = ({
  showModal,
  onClose,
  editingBillingInfo,
  selectedUserCompany,
  onSave,
}) => {
  const [billingFormData, setBillingFormData] = useState<BillingInfo>(
    editingBillingInfo || {
      billing_id: 0,
      company_id: selectedUserCompany?.company_id || 0,
      billingEmail: "",
      iban: "",
      poNumber: null,
      twikeyRef: "",
      active_dd_mandate: 0,
      eol_dd_mandate_uploaded: 0,
      twikeyDate: null,
      twikeyCode: null,
      twikeyValid: 0,
      billingAddressStreet: "",
      billingAddressNumber: "",
      billingAddressZip: "",
      billingAddressCity: "",
      billingAddressCountry: "",
      invoicingCycle: "Monthly",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
  );

  const [ibanError, setIbanError] = useState<string>("");
  const [ibanValid, setIbanValid] = useState<boolean | null>(null);

  useEffect(() => {
    if (editingBillingInfo) {
      setBillingFormData(editingBillingInfo);
    } else {
      setBillingFormData({
        billing_id: 0,
        company_id: selectedUserCompany?.company_id || 0,
        billingEmail: "",
        iban: "",
        poNumber: null,
        twikeyRef: "",
        active_dd_mandate: 0,
        eol_dd_mandate_uploaded: 0,
        twikeyDate: null,
        twikeyCode: null,
        twikeyValid: 0,
        billingAddressStreet: "",
        billingAddressNumber: "",
        billingAddressZip: "",
        billingAddressCity: "",
        billingAddressCountry: "",
        invoicingCycle: "Monthly",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }
  }, [editingBillingInfo, selectedUserCompany]);

  const validateIBAN = (value: string) => {
    // Remove spaces and convert to uppercase
    const cleanIBAN = value.replace(/\s/g, "").toUpperCase();

    if (!cleanIBAN) {
      setIbanError("");
      setIbanValid(null);
      return;
    }

    // Check if IBAN is valid
    if (IBAN.isValid(cleanIBAN)) {
      setIbanError("");
      setIbanValid(true);

      // Get country code from IBAN
      const countryCode = cleanIBAN.substring(0, 2);
      const countryName = getCountryFromIBAN(countryCode);

      // Auto-fill country if detected
      if (countryName && !billingFormData.billingAddressCountry) {
        setBillingFormData((prev) => ({ ...prev, billingAddressCountry: countryName }));
      }
    } else {
      setIbanError("Invalid IBAN. Please check and try again.");
      setIbanValid(false);
    }
  };

  const getCountryFromIBAN = (code: string): string => {
    const countries: { [key: string]: string } = {
      BE: "Belgium",
      NL: "Netherlands",
      DE: "Germany",
      FR: "France",
      ES: "Spain",
      IT: "Italy",
      GB: "United Kingdom",
      CH: "Switzerland",
      AT: "Austria",
      LU: "Luxembourg",
      // Add more countries as needed
    };
    return countries[code] || "";
  };

  const formatIBAN = (value: string) => {
    // Remove all spaces first
    const cleanValue = value.replace(/\s/g, "").toUpperCase();

    // Add spaces every 4 characters for better readability
    const formatted = cleanValue.match(/.{1,4}/g)?.join(" ") || cleanValue;

    return formatted;
  };

  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatIBAN(e.target.value);
    setBillingFormData((prev) => ({ ...prev, iban: formattedValue }));
    validateIBAN(formattedValue);
  };

  if (!showModal) return null;

  const handleSaveBilling = () => {
    // Only validate IBAN if it's provided
    if (billingFormData.iban && billingFormData.iban.trim()) {
      if (!IBAN.isValid(billingFormData.iban.replace(/\s/g, ""))) {
        setIbanError("Please enter a valid IBAN before saving.");
        return;
      }
    }

    onSave(billingFormData);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-bold text-primary-600 mb-4">
                {editingBillingInfo ? "Update Billing Information" : "Add Billing Information"}
              </h3>

              <div className="mt-4 space-y-4">
                {/* Billing Email */}
                <div>
                  <label className="block text-sm font-medium text-primary-600 mb-1">Billing Email *</label>
                  <input
                    type="email"
                    value={billingFormData.billingEmail}
                    onChange={(e) => setBillingFormData((prev) => ({ ...prev, billingEmail: e.target.value }))}
                    className="w-full border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200"
                    required
                  />
                </div>

                {/* IBAN with validation */}
                <div>
                  <label className="block text-sm font-medium text-primary-600 mb-1">
                    IBAN *{ibanValid === true && <span className="ml-2 text-green-600 text-xs">✓ Valid</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={billingFormData.iban}
                      onChange={handleIBANChange}
                      placeholder="e.g., BE68 5390 0754 7034"
                      maxLength={34} // Maximum IBAN length
                      className={`w-full border rounded-md px-3 py-2 pr-10 text-primary-400 text-sm focus:outline-none focus:ring ${
                        ibanValid === false
                          ? "border-red-400 focus:ring-red-200"
                          : ibanValid === true
                          ? "border-green-400 focus:ring-green-200"
                          : "border-gray-200 focus:ring-primary-200"
                      }`}
                      required
                    />
                    {ibanValid !== null && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        {ibanValid ? (
                          <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                  {ibanError && <p className="mt-1 text-xs text-red-600">{ibanError}</p>}
                  {ibanValid === true && billingFormData.iban && (
                    <p className="mt-1 text-xs text-gray-500">
                      Country: {getCountryFromIBAN(billingFormData.iban.substring(0, 2))}
                    </p>
                  )}
                </div>

                {/* Address Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary-600 mb-1">Street *</label>
                    <input
                      type="text"
                      value={billingFormData.billingAddressStreet}
                      onChange={(e) =>
                        setBillingFormData((prev) => ({ ...prev, billingAddressStreet: e.target.value }))
                      }
                      className="w-full border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary-600 mb-1">Number *</label>
                    <input
                      type="text"
                      value={billingFormData.billingAddressNumber}
                      onChange={(e) =>
                        setBillingFormData((prev) => ({ ...prev, billingAddressNumber: e.target.value }))
                      }
                      className="w-full border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primary-600 mb-1">ZIP Code *</label>
                    <input
                      type="text"
                      value={billingFormData.billingAddressZip}
                      onChange={(e) => setBillingFormData((prev) => ({ ...prev, billingAddressZip: e.target.value }))}
                      className="w-full border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary-600 mb-1">City *</label>
                    <input
                      type="text"
                      value={billingFormData.billingAddressCity}
                      onChange={(e) => setBillingFormData((prev) => ({ ...prev, billingAddressCity: e.target.value }))}
                      className="w-full border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-600 mb-1">Country *</label>
                  <input
                    type="text"
                    value={billingFormData.billingAddressCountry}
                    onChange={(e) => setBillingFormData((prev) => ({ ...prev, billingAddressCountry: e.target.value }))}
                    className="w-full border rounded-md px-3 py-2 border-gray-200 text-primary-400 text-sm focus:outline-none focus:ring focus:ring-primary-200"
                    required
                  />
                </div>

              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  onClick={handleSaveBilling}
                  // disabled={ibanValid === false || !billingFormData.iban}
                >
                  Save Billing Information
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
